import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Card, Col, Modal, Row, Space, Table, Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { RiAddLine, RiCloseFill } from 'react-icons/ri';
import { Filter } from 'iconsax-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import urlPagePushNotificationScheduleCreate from '../../../../../../urls/urlPagePushNotificationScheduleCreate';
import urlPagePushNotificationScheduleEdit from '../../../../../../urls/urlPagePushNotificationScheduleEdit';
import urlPagePushNotifications from '../../../../../../urls/urlPagePushNotifications';
import urlPagePushNotificationScheduleClone from '../../../../../../urls/urlPagePushNotificationScheduleClone';
import IntlMessages from '../../../../../../layout/components/lang/IntlMessages';
import basePagination from '../../../../../../const/pagination';
import { getPushNotificationsScheduleTableColumns } from './PagePushNotificationsSchedule.const';
import { PrivateComponent } from '../../../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../../../const/permissions';
import useToggle from '../../../../../../app/hooks/useToggle';
import useQueryParams from '../../../../../../app/hooks/useQueryParams';
import useDebounce from '../../../../../../app/hooks/useDebounce';
import makeTableLoadingObject from '../../../../../../lib/makeTableLoadingObject';
import useElementSize from '../../../../../../app/hooks/useElementSize';
import applyFilter from '../../../../../../lib/applyFilter';
import resetFilter from '../../../../../../lib/resetFilter';
import handleResponse from '../../../../../../lib/handleResponse';
import getPushNotificationOptions from '../../../../../../lib/getPushNotificationOptions';
import BreadCrumbs from '../../../../../../layout/components/breadcrumbs';
import ActionButton from '../../../../../../layout/components/action-button';
import Sidebar from '../../../../../../components/Sidebar';
import DocumentationTooltip from '../../../../../../components/DocumentationTooltip/DocumentationTooltip';
import FormNotificationsScheduleFilter from '../../../../forms/FormNotificationsScheduleFilter';
import { useGetLanguagesQuery } from '../../../../../Translations/api/languagesApiSlice';
import { useGetPushNotificationsQuery } from '../../../../api/pushNotificationsApiSlice';
import {
  useDeletePushNotificationScheduleMutation,
  useGetPushNotificationsScheduleQuery,
} from '../../../../api/pushNotificationsScheduleApiSlice';



const { Title } = Typography;

const PagePushNotificationsSchedule = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [ searchQueryParams, setSearchParams ] = useSearchParams();
  const [ elementRef, size ] = useElementSize();
  const [ previewImage, setPreviewImage ] = useState('');
  const [ pushNotificationTitle, setPushNotificationTitle ] = useState('');
  const searchQuery = useDebounce(pushNotificationTitle.trim(), 200);
  const [ isVisibleFilterSidebar, toggleFilterSidebar ]  = useToggle();
  const [ previewModalOpen, togglePreviewModal ] = useToggle();

  const searchFilterFields = [ 'id', 'push_notification', 'scheduled_from', 'scheduled_to', 'is_published', 'countries', 'min_version', 'max_version', 'platforms', 'languages', 'use_utc_zero_time', 'utc_from', 'utc_to' ];

  const onSearchByTitle = useCallback((searchText) => {
    setPushNotificationTitle(searchText);
  }, [ pushNotificationTitle ]);

  const {
    pagination,
    setSearchTerm,
    searchParams,
    initFilterValues,
    setInitFilterValues,
    handleChangeTableParams,
    resetIsManuallyTriggered,
    setResetManuallyTriggered,
  } = useQueryParams({ searchFilterFields });

  const searchParamsString = searchParams.toString();

  const { data: {
    data: pushNotificationSchedules = [],
    pagination: pushNotificationsPagination = {},
  } = { data: [], pagination: [] },
  isFetching,
  error: formErrors = {},
  } = useGetPushNotificationsScheduleQuery({ queryParams: `${searchParamsString}` }, { skip: !searchParamsString });

  const { data: {
    data: languages = [],
  } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' });

  const pushNotificationQueryParams = searchQuery ? `&search=title:${searchQuery}` : '';
  const { data: {
    data: pushNotifications = [],
  } = { data: [] },
  } = useGetPushNotificationsQuery({ queryParams: `page=1&limit=100&${pushNotificationQueryParams}` });

  const getFilteredPushNotificationOptions = getPushNotificationOptions(pushNotifications.filter((item) => item.has_schedules));

  const [ deletePushNotificationSchedule, { isLoading: isPushNotificationScheduleDeleting } ] = useDeletePushNotificationScheduleMutation();

  const handleEdit = (id) => {
    navigate(urlPagePushNotificationScheduleEdit({ scheduleId: id }));
  };

  const handleDelete = (id) => {
    deletePushNotificationSchedule(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleClone = (id) => {
    navigate(urlPagePushNotificationScheduleClone({ scheduleId: id }));
  };

  const handleCreate = () => {
    navigate(urlPagePushNotificationScheduleCreate());
  };

  const handleImageCardClick = (preview) => {
    setPreviewImage(preview);
    togglePreviewModal();
  };

  const handlePreviewModalClose = () => {
    togglePreviewModal();
  };

  return (
    <>
      <Modal
        width={500}
        title={intl.formatMessage({ id: 'content-image-preview-card-title' })}
        centered
        visible={previewModalOpen}
        onCancel={handlePreviewModalClose}
        footer={false}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <img alt="icon" src={previewImage} />
      </Modal>

      <Row
        gutter={[ 32, 32 ]}
        justify="space-between"
        className="hp-mb-32"
        align="middle"
      >
        <BreadCrumbs
          breadCrumbParent={
            <Link to={urlPagePushNotifications()}>
              <IntlMessages id='push-notifications-breadcrumbs' />
            </Link>
          }
          breadCrumbActive={
            <div>
              <IntlMessages id='push-notifications-schedule-breadcrumbs' />
              <DocumentationTooltip link="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/1452933126/Schedule" />
            </div>
          }
        />

        <Col md={16} span={24}>
          <Row
            gutter={[ 32, 32 ]}
            justify="end"
            align="middle"
          >
            <Col>
              <Space>
                <PrivateComponent allowedPermissions={[ Permissions.OTHER.NOTIFICATIONS.PUSH.SCHEDULE.CREATE ]}>
                  <ActionButton
                    title={<IntlMessages id='ui-general-create' />}
                    icon={<RiAddLine className="btn-icon-mr-1" />}
                    onClick={handleCreate}
                  />
                </PrivateComponent>
                <ActionButton
                  title=""
                  isActive={!isEmpty(initFilterValues)}
                  icon={<Filter size={18} data-testid="filter-action-icon" />}
                  onClick={toggleFilterSidebar}
                />
              </Space>
            </Col>
          </Row>
        </Col>

        <Sidebar
          title={<Title level={5}>{intl.formatMessage({ id: 'push-notifications-schedule-filter-title' })}</Title>}
          visible={isVisibleFilterSidebar}
          toggleSidebar={toggleFilterSidebar}
        >
          <FormNotificationsScheduleFilter
            initialValues={initFilterValues}
            languages={languages}
            pushNotificationOptions={getFilteredPushNotificationOptions}
            formErrors={formErrors}
            isSubmitting={false}
            onSearchByTitle={onSearchByTitle}
            onCancel={() => {
              setResetManuallyTriggered(true);

              resetFilter({
                searchQueryParams,
                setSearchParams,
                setInitFilterValues,
                resetIsManuallyTriggered,
                setSearchTerm,
              });
            }}
            onSubmit={(values) => {
              applyFilter({
                values,
                searchQueryParams,
                searchFilterFields,
                setSearchParams,
                setSearchField: setSearchTerm,
                toggleFilterSidebar,
              });
            }}
          />
        </Sidebar>
      </Row>

      <Row gutter={[ 32, 32 ]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <div ref={elementRef}>
              <Table
                sticky
                scroll={{ x: 860 }}
                loading={makeTableLoadingObject(isFetching || isPushNotificationScheduleDeleting)}
                rowKey="id"
                columns={getPushNotificationsScheduleTableColumns(
                  handleEdit,
                  handleDelete,
                  handleClone,
                  handleImageCardClick,
                  size,
                )}
                dataSource={pushNotificationSchedules}
                onChange={handleChangeTableParams}
                pagination={{
                  ...basePagination,
                  current: pagination.page,
                  pageSize: pagination.limit,
                  total: pushNotificationsPagination?.total,
                }}
              />
            </div>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PagePushNotificationsSchedule;
